import { FC } from 'react';
import { Button, Menu } from 'react-daisyui';
import { cn } from '@/utils/classNames.ts';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { useTranslation } from 'react-i18next';
import { nonprofitNavData, superadminNavData, childUserNavData, NavItem } from '@/layouts/Default/config-nav.tsx';
import { usePathname } from '@/hooks/usePathname.ts';
import { Link } from 'react-router-dom';
import { ArrowLeftStartOnRectangleIcon } from '@heroicons/react/24/solid';

type NavbarProps = {
  onClick?: () => void;
  menuClassName?: string;
};

const Navbar: FC<PropsWithClassName<NavbarProps>> = ({ className, onClick, menuClassName }) => {
  const { logout, userType, userLoading } = useAuthContext();
  const { t } = useTranslation();
  const pathname = usePathname();

  const getNavData = (): NavItem[] => {
    switch (userType) {
      case 'superadmin':
        return superadminNavData;
      case 'nonprofit':
        return nonprofitNavData;
      case 'child_user':
        return childUserNavData;
      default:
        return [];
    }
  };

  const currentNavData = getNavData();

  if (userLoading) {
    return (
      <nav className={cn('flex flex-col gap-4 justify-between grow', className)}>
        <div className="flex items-center justify-center p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
        </div>
      </nav>
    );
  }

  return (
    <nav className={cn('flex flex-col gap-4 justify-between grow', className)}>
      <div className="flex flex-col grow gap-6">

        <Menu className={cn('p-0 overflow-auto flex-nowrap grow h-1', menuClassName)}>
          {currentNavData.length === 0 ? (
            <Menu.Item>
              <div className="text-gray-500 text-center py-4">
                {t('navbar.noNavigation')}
              </div>
            </Menu.Item>
          ) : (
            currentNavData.map((item) => {
              // Check if current path matches main path or any activePaths
              const isActivated = pathname.startsWith(item.path) || 
                (item.activePaths?.some(activePath => pathname.startsWith(activePath)) ?? false);

              return (
                <Menu.Item key={item.path}>
                  <Link
                    to={item.path}
                    title={t(item.title)}
                    onClick={() => (onClick ? onClick() : null)}
                    className={cn(
                      'mb-1 active:bg-neutral active:text-neutral-content hover:bg-base-300 hover:text-base-content flex gap-2',
                      {
                        'bg-neutral text-neutral-content': isActivated,
                      }
                    )}
                  >
                    {typeof item.icon === 'string' ? (
                      <span className="w-4 h-4 text-center">{item.icon}</span>
                    ) : item.icon ? (
                      <div className="w-4 h-4">{item.icon}</div>
                    ) : null}
                    <span className="text-ellipsis line-clamp-1">{t(item.title)}</span>
                  </Link>
                </Menu.Item>
              );
            })
          )}
        </Menu>
      </div>
      
      <div className="flex flex-col border-t border-gray-200 pt-4">
        <Button
          onClick={logout}
          color="ghost"
          className="!gap-4"
          startIcon={<ArrowLeftStartOnRectangleIcon className="w-4" />}
        >
          {t('button.logout')}
        </Button>
      </div>
    </nav>
  );
};

export default Navbar;